// Bitácora de jornada (una unidad, un día): formato y armado, puro y con tests
// (bitacora.test.ts). Reglas en reportes/conduccion-spec/ESPECIFICACION.txt
// §1.1 y §1.6: el panel y el archivo salen de las mismas funciones.
import { addDays, dateInTz, userTz } from "../quickReports.compute";
import type { DailyUnitSummary, QuickDriver, QuickEventCounts, Segment, SegmentState } from "../types";

export const STATE_LABEL: Record<SegmentState, string> = {
  driving: "Conduciendo",
  maneuver: "Maniobrando",
  idle: "Ralentí",
  stopped: "Detenido",
  rest: "Descanso",
  no_data: "Sin datos",
};

// §1.1.5: verde · azul · amarillo · gris · índigo · gris claro. Descanso era
// "color principal", pero con el tema Dumax (verde) chocaba con Conduciendo y
// con grayscale con Detenido: índigo fijo, decisión de Daniel (17-oct-2026).
export const STATE_COLOR: Record<SegmentState, { light: string; dark: string }> = {
  driving: { light: "bg-green-500", dark: "bg-green-500" },
  maneuver: { light: "bg-blue-500", dark: "bg-blue-500" },
  idle: { light: "bg-yellow-400", dark: "bg-yellow-400" },
  stopped: { light: "bg-gray-500", dark: "bg-gray-500" },
  rest: { light: "bg-indigo-500", dark: "bg-indigo-400" },
  no_data: { light: "bg-gray-200", dark: "bg-white/15" },
};

// ---------- Duraciones ----------

// Panel: "X h MM min"; menos de una hora, "M min" (redondeado al minuto).
export const fmtJornada = (s: number): string => {
  const min = Math.round(Math.max(0, s || 0) / 60);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, "0")} min`;
};

// Archivo: "HHhrs MMmin SSseg".
export const fmtFileDuration = (s: number): string => {
  const sec = Math.max(0, Math.round(s || 0));
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(Math.floor(sec / 3600))}hrs ${p(Math.floor((sec % 3600) / 60))}min ${p(sec % 60)}seg`;
};

// hourCycle h23 y no hour12:false: con éste, algunos ICU (Node 20) dan "24:00"
// para la medianoche.
export const fmtHM = (ts: number, tz: string) =>
  new Intl.DateTimeFormat("es-MX", { timeZone: tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(ts);

// "2026-10-16" → "16/10/2026"
export const fmtDMY = (date: string) => date.split("-").reverse().join("/");

// ---------- Fecha ----------

// Zona del usuario: el día va de 00:00 a 23:59 en su hora local (§1.1.1).
export { userTz };

export const todayIn = (tz: string, now = Date.now()) => dateInTz(now, tz);
// Nunca un día futuro.
export const clampDay = (date: string, today: string) => (date > today ? today : date);
export const shiftDay = (date: string, n: number, today: string) => clampDay(addDays(date, n), today);

// "jueves 16 oct 2026"
export const fmtLongDay = (date: string) => {
  const [y, m, d] = date.split("-").map(Number);
  return new Intl.DateTimeFormat("es-MX", { timeZone: "UTC", weekday: "long", day: "numeric", month: "short", year: "numeric" })
    .format(Date.UTC(y, m - 1, d))
    .replace(/\./g, "");
};

// Una respuesta sólo se pinta si es del día y la unidad que se están viendo: si
// el usuario cambió de fecha mientras cargaba, la vieja se descarta (§1.1).
export const pickDay = (items: DailyUnitSummary[] | undefined, deviceId: string, date: string) =>
  items?.find((it) => it.device_id === deviceId && it.date === date) ?? null;

export const isEmptyDay = (day: DailyUnitSummary | null) => !day || (day.points_count === 0 && day.segments.length === 0);

// ---------- Conductor ----------

export const driverCard = (d: QuickDriver | null) => {
  if (!d) return null;
  const shift = [d.shift_hours != null ? `${d.shift_hours} h` : null, d.shift_days != null ? `${d.shift_days} d` : null].filter(Boolean).join(" · ");
  return {
    name: d.name,
    photo: d.photo_url,
    initials: d.name
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join(""),
    license: d.license_number,
    expires: d.license_expires_at ? `vence ${fmtDMY(d.license_expires_at)}` : null,
    shift: shift || null,
  };
};

// ---------- Indicadores (§1.1.4) ----------

export const indicators = (day: DailyUnitSummary) => [
  { key: "km", label: "Kilómetros", value: `${(day.distance_km || 0).toFixed(1)} km` },
  { key: "driving", label: "Conduciendo", value: fmtJornada(day.driving_s) },
  { key: "idle", label: "Ralentí", value: fmtJornada(day.idle_s) },
  { key: "rest", label: "Descanso", value: fmtJornada(day.rest_s) },
  { key: "fuel", label: "Combustible", value: day.fuel ? `${Math.round(day.fuel.start_pct)}% → ${Math.round(day.fuel.end_pct)}%` : "—" },
  { key: "kmpl", label: "Rendimiento", value: day.fuel?.km_per_l && day.fuel.km_per_l > 0 ? `${day.fuel.km_per_l.toFixed(2)} km/L` : "—" },
];

// ---------- Barra del día (§1.1.5) ----------

export const dayBar = (segments: Segment[]) => {
  const total = segments.reduce((a, s) => a + Math.max(0, s.duration_s), 0);
  return total
    ? segments.map((s) => ({ state: s.state, estimated: Boolean(s.estimated), pct: (Math.max(0, s.duration_s) / total) * 100, start_ts: s.start_ts, end_ts: s.end_ts }))
    : [];
};

// Nombre del tramo: el manejo estimado se distingue del visto.
export const segmentLabel = (s: { state: SegmentState; estimated?: boolean }) =>
  s.estimated ? "Manejo (estimado)" : STATE_LABEL[s.state];

// Rayado sobre el color del estado para lo estimado (barra y ícono del tramo).
export const ESTIMATED_STYLE = {
  backgroundImage: "repeating-linear-gradient(135deg, rgba(255,255,255,0.45) 0 3px, transparent 3px 7px)",
} as const;

// ---------- Contador de eventos (§1.1.6) ----------

export const eventChips = (c: QuickEventCounts | null | undefined) =>
  !c
    ? []
    : [
        { key: "overspeed", label: "Excesos", count: c.overspeed },
        { key: "harsh_brake", label: "Frenados", count: c.harsh_brake },
        { key: "harsh_accel", label: "Acelerones", count: c.harsh_accel },
        { key: "camera", label: "Cámara", count: c.camera },
        { key: "panic", label: "Pánico", count: c.panic },
        { key: "jamming", label: "Pérdida de señal", count: c.jamming },
      ].filter((x) => x.count > 0);

// ---------- Renglón de tramo (§1.1.7) ----------

// Dirección de un tramo detenido (Detenido / Descanso); en movimiento no aplica.
export const stopAddress = (s: Segment) => (s.state === "stopped" || s.state === "rest" ? s.start?.address || null : null);

export const segmentRow = (s: Segment, tz: string) => ({
  title: segmentLabel(s),
  range: `${fmtHM(s.start_ts, tz)} – ${fmtHM(s.end_ts, tz)}`,
  detail: [
    fmtJornada(s.duration_s),
    s.geofence?.name ?? null,
    s.distance_km > 0.1 ? `${s.distance_km.toFixed(1)} km` : null,
    s.avg_speed_kmh > 0 ? `${Math.round(s.avg_speed_kmh)} km/h` : null,
  ]
    .filter(Boolean)
    .join(" · "),
  max: s.max_speed_kmh > 0 ? `máx ${Math.round(s.max_speed_kmh)}` : null,
  // Aparte del detalle para abreviarla con ellipsis sin cortar duración ni geocerca.
  address: stopAddress(s),
});

// ---------- Archivo (§1.6) ----------

export const BITACORA_TITLE = "Bitácora de jornada";

export const FILE_COLUMNS = [
  { key: "unit", header: "Unidad" },
  { key: "driver", header: "Conductor" },
  { key: "date", header: "Fecha" },
  { key: "state", header: "Estado" },
  { key: "from", header: "Desde" },
  { key: "to", header: "Hasta" },
  { key: "duration", header: "Duración" },
  { key: "place", header: "Lugar" },
  { key: "avg", header: "Vel. promedio" },
  { key: "max", header: "Vel. máxima" },
  { key: "distance", header: "Distancia" },
] as const;

export type FileRow = { id: string } & Record<(typeof FILE_COLUMNS)[number]["key"], string>;

export const fileRows = (day: DailyUnitSummary, tz: string): FileRow[] =>
  day.segments.map((s, i) => ({
    id: `${day.device_id}-${day.date}-${i}`,
    unit: day.unit_name,
    driver: day.driver?.name ?? "—",
    date: fmtDMY(day.date),
    state: segmentLabel(s),
    from: fmtHM(s.start_ts, tz),
    to: fmtHM(s.end_ts, tz),
    duration: fmtFileDuration(s.duration_s),
    place: s.geofence?.name ?? stopAddress(s) ?? "—",
    avg: s.avg_speed_kmh > 0 ? `${Math.round(s.avg_speed_kmh)} km/h` : "—",
    max: s.max_speed_kmh > 0 ? `${Math.round(s.max_speed_kmh)} km/h` : "—",
    distance: s.distance_km > 0 ? `${s.distance_km.toFixed(2)} km` : "—",
  }));

// bitacora_{unidad}_{YYYY-MM-DD} (sin extensión: ExportButtons agrega .pdf/.xlsx).
// Sólo se quitan los caracteres que un sistema de archivos no acepta.
export const fileBase = (unit: string, date: string) => `bitacora_${unit.replace(/[\\/:*?"<>|]/g, "-").trim()}_${date}`;
